import { Trans } from 'react-i18next';
import { getSocialMediaText } from '@anytag/shared-utils';
import { SelectorField, TextareaField } from '@anytag/shared-molecules';
import { BaseModal, Form, ModalFooter, ModalBody } from '@anytag/shared-atoms';
import { useToggleState, useQueryHelper, useSelectItem } from '@anytag/shared-hooks';
import {
  namedOperations,
  TikTokSpecialPostDetail,
  CampaignSocialMediaType,
  useReportTiktokSpecialPostStatusMutation
} from '@anytag/graphql';

interface RejectFormValues {
  reason: string;
  detailedReason: string;
}

export const useRejectTikTokCampaignPost = () => {
  const loadingState = useToggleState();
  const { t, enqueueSnackbar } = useQueryHelper();
  const { selectedItem, setSelectedItem, resetSelectedItem } = useSelectItem<TikTokSpecialPostDetail>();
  const { callReportTiktokSpecialPostStatus } = useReportTiktokSpecialPostStatusMutation({
    refetchQueries: [
      namedOperations.Query.TiktokSpecialCampaignPosts,
      namedOperations.Query.TiktokSpecialPost,
      namedOperations.Query.TiktokSpecialPostHistory
    ]
  });

  const hasAskToEdit = !!selectedItem;
  const hasAskToRePost = !!selectedItem;

  const handleSubmit = async ({ reason, detailedReason }: RejectFormValues) => {
    if (!selectedItem?.id) return;

    loadingState.open();

    try {
      await callReportTiktokSpecialPostStatus({
        variables: { input: { id: selectedItem.id, reason, detailedReason } }
      });
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      loadingState.close();
      resetSelectedItem();
    }
  };

  return {
    reporting: loadingState.status,
    handleRejectPost: setSelectedItem,
    RejectMarketplacePostModal: () => (
      <BaseModal open={!!selectedItem?.id} onClose={resetSelectedItem} title={t('Dialog.Report Inappropriate')} asChild>
        <Form.Provider<RejectFormValues>
          onSubmit={handleSubmit}
          defaultValues={{ reason: selectedItem?.reason || '', detailedReason: selectedItem?.detailedReason || '' }}
        >
          {({ values }) => (
            <>
              <ModalBody>
                {hasAskToEdit || hasAskToRePost ? (
                  <p css={{ marginBottom: '16px' }}>
                    {hasAskToEdit ? (
                      <>
                        {getSocialMediaText(CampaignSocialMediaType.TIKTOK)}&nbsp;
                        <Trans i18nKey="Dialog.ReportInappropriateEditDescription" components={{ b: <b /> }} />
                      </>
                    ) : hasAskToRePost ? (
                      <Trans i18nKey="Dialog.ReportInappropriateRePostDescription" components={{ b: <b /> }} />
                    ) : null}
                  </p>
                ) : null}

                <SelectorField<keyof RejectFormValues>
                  name="reason"
                  title={t('Selector.Reason')}
                  options={[
                    { label: t<string>('Option.Inappropriate contents'), value: 'Inappropriate contents' },
                    {
                      label: t<string>('Option.Doesn’t match the conditions'),
                      value: 'Doesn’t match the conditions'
                    }
                  ]}
                  required
                />

                <TextareaField<keyof RejectFormValues>
                  name="detailedReason"
                  title={t('TextForm.Detailed Reason')}
                  help={t('Tooltip.Detailed Reason Help')}
                  placeholder={t<string>('TextForm.Detailed Reason Placeholder')}
                  required
                />
              </ModalBody>

              <ModalFooter
                cancelButton={{ onClick: resetSelectedItem }}
                submitButton={{
                  type: 'submit',
                  title: t('Button.Send'),
                  loading: loadingState.status,
                  disabled: !values.reason || !values.detailedReason
                }}
              />
            </>
          )}
        </Form.Provider>
      </BaseModal>
    )
  };
};
